import { createContext, useContext, useCallback, ReactNode } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useLanguage } from '@/contexts/LanguageContext';
import { logger } from '@/services/logger';
import type { Translations } from '@/i18n/types';
import { ErrorProblemDetailsErrorCode } from '@/api/control/missionImpactAPI.schemas';
import type { ErrorProblemDetails, ProfileDto } from '@/api/control/missionImpactAPI.schemas';
import { getRegionNatIp } from '@/constants/regions';

/**
 * Canonical type alias for service error codes.
 * Orval generates identical enums per DTO (ErrorProblemDetailsErrorCode, RunMetadataDtoErrorCode, etc.).
 * Use this single alias everywhere so call-sites don't depend on a specific DTO's variant.
 */
export type ServiceErrorCode = ErrorProblemDetailsErrorCode;

/**
 * Context parameter for handleApiError, providing the logging label and optional profile
 * for error message interpolation (e.g. firewall IP replacement).
 */
export interface ApiErrorContext {
  context: string;
  profile?: ProfileDto;
}

/**
 * Result returned by handleApiError so callers can branch on specific error codes
 */
interface ApiErrorResult {
  message: string;
  errorCode: ServiceErrorCode | undefined;
}

interface ErrorContextType {
  /** Single entry point for error handling. Logs, optionally toasts, returns translated message. */
  handleApiError: (error: unknown, context: ApiErrorContext, options?: { showToast?: boolean }) => ApiErrorResult;
  /** Translate a known error code to a user-facing message. Returns undefined if no translation exists. */
  translateErrorCode: (errorCode: ServiceErrorCode | null | undefined, profile?: ProfileDto) => string | undefined;
}

const ErrorContext = createContext<ErrorContextType | undefined>(undefined);

/**
 * Look up a translated message by error code, then HTTP status, then network heuristic.
 * Raw API messages are never returned -- only translations.
 */
function getTranslatedMessage(
  errorCode: ServiceErrorCode | undefined,
  status: number | undefined,
  axiosCode: string | undefined,
  t: Translations,
): string {
  // 1. Try error code translation
  if (errorCode != null && errorCode in t.errors.codes) {
    return t.errors.codes[errorCode as keyof typeof t.errors.codes];
  }

  // 2. Try HTTP status translation
  if (status) {
    switch (status) {
      case 401:
        return t.errors.common.unauthorized;
      case 403:
        return t.errors.common.forbidden;
      case 404:
        return t.errors.common.notFound;
      case 500:
      case 502:
      case 503:
        return t.errors.common.serverError;
    }
  }

  // 3. Network errors
  if (axiosCode === 'ERR_NETWORK') {
    return t.errors.common.networkError;
  }

  // 4. Fallback
  return t.errors.codes.unknown;
}

/**
 * Replaces placeholders in translated error messages based on the error code.
 * For AzureSqlFirewallBlocked, substitutes {ip} with the region's NAT IP.
 */
function interpolateErrorMessage(
  message: string,
  errorCode: ServiceErrorCode | undefined,
  profile?: ProfileDto,
): string {
  if (errorCode === 'AzureSqlFirewallBlocked' && profile?.regionId) {
    const natIp = getRegionNatIp(profile.regionId);
    if (natIp) return message.replace('{ip}', natIp);
  }
  return message;
}

/**
 * Provider component that centralizes error handling with access to translations
 */
export function ErrorProvider({ children }: { children: ReactNode }) {
  const { t } = useLanguage();

  /**
   * Translate a known error code to a user-facing message.
   * Pure lookup -- no logging, no toasting.
   * Returns undefined if no translation exists for the given code.
   */
  const translateErrorCode = useCallback(
    (errorCode: ServiceErrorCode | null | undefined, profile?: ProfileDto): string | undefined => {
      if (errorCode == null) return undefined;
      if (errorCode in t.errors.codes) {
        const message = t.errors.codes[errorCode as keyof typeof t.errors.codes];
        return interpolateErrorMessage(message, errorCode, profile);
      }
      return undefined;
    },
    [t],
  );

  /**
   * Single entry point for API error handling.
   * Logs, optionally toasts, returns a translated user-facing message and the raw error code.
   */
  const handleApiError = useCallback(
    (error: unknown, ctx: ApiErrorContext, options?: { showToast?: boolean }): ApiErrorResult => {
      const showToast = options?.showToast ?? true;

      let status: number | undefined;
      let axiosCode: string | undefined;
      let errorCode: ServiceErrorCode | undefined;

      if (axios.isAxiosError<ErrorProblemDetails>(error)) {
        // Response is a typed ErrorProblemDetails -- extract the service error code
        status = error.response?.status;
        axiosCode = error.code;
        errorCode = error.response?.data.errorCode ?? undefined;
      } else if (axios.isAxiosError(error)) {
        // Generic Axios error -- fall back to HTTP status / network heuristics
        status = error.response?.status;
        axiosCode = error.code;
      }

      const rawMessage = getTranslatedMessage(errorCode, status, axiosCode, t);
      const message = interpolateErrorMessage(rawMessage, errorCode, ctx.profile);

      // Log with raw details for debugging
      logger.error(
        error instanceof Error ? error : new Error(String(error)),
        { context: ctx.context, errorCode, status, code: axiosCode },
      );

      if (showToast) {
        toast.error(message);
      }

      return { message, errorCode };
    },
    [t],
  );

  return (
    <ErrorContext.Provider value={{ handleApiError, translateErrorCode }}>
      {children}
    </ErrorContext.Provider>
  );
}

/**
 * Hook to access error context
 * @throws Error if used outside of ErrorProvider
 */
export function useError() {
  const context = useContext(ErrorContext);
  if (context === undefined) {
    // eslint-disable-next-line no-restricted-syntax
    throw new Error('useError must be used within an ErrorProvider');
  }
  return context;
}
